import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Placeholder } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { Row } from 'react-table';
import styled from 'styled-components';

import StatusDefault from 'app/components/Status/StatusDefault';
import TableComponent from 'app/components/Table/TableComponent';
import { QUERIES } from 'app/components/Table/core/constants';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';
import Typography from 'app/components/Typography';
import RootContentPage from 'app/components/layout/RootContentPage';

import AssignedItemCard from 'app/modules/inventory/items/components/AssignedItemCard';
import ItemStatusModal from 'app/modules/inventory/items/components/ItemStatusModal';
import UnableInactiveItemModal from 'app/modules/inventory/items/components/UnableInactiveItemModal';
import { StatusItem } from 'app/modules/inventory/items/core/constants';
import { LocationTitle } from 'app/modules/inventory/items/core/styles';
import { getSetting } from 'app/modules/system-settings/configuration/core/request';
import {
  SettingType,
  StockLevelSettingType,
} from 'app/modules/system-settings/configuration/core/type';

import { usePageGoBack } from 'app/helpers/utils';
import { useNumber } from 'app/hooks';

import { PATH } from 'app/constants/path';

import { ReactComponent as IconRedirect } from 'app/assets/icons/icon-redirect.svg';

import ShowInfoItemCard from './components/ShowInfoItemCard';
import { stockValueSettingColumns } from './core/columns';
import { StockTypeEnum } from './core/enum';
import { getItemTable } from './core/requests';
import { Item } from './core/type';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const Header = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Main = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;
`;

const ParLavelSettingContainer = styled.div`
  position: relative;
  padding-top: 28px;
  background: var(--white);
  width: 50%;

  tbody tr td {
    padding: 1px 12px 1px 28px !important;
  }

  .pagination {
    position: absolute;
    bottom: 28px;
  }
`;

const AssignItemContainer = styled.section`
  position: relative;
  padding-top: 28px;
  background: var(--white);
  width: 50%;

  .card {
    border-top: none !important;
  }

  .card-header {
    justify-content: flex-end;
  }

  .modal-dialog {
    min-width: 1000px !important;
  }

  .modal-body {
    padding: 28px 40px 28px 20px !important;
  }

  .pagination {
    position: absolute;
    bottom: 28px;
  }

  @media (max-width: 1200px) {
    .modal-dialog {
      min-width: 800px !important;
    }
  }

  @media (max-width: 992px) {
    .modal-dialog {
      min-width: 600px !important;
    }
  }
`;

export const StockValueSettingTableContainerStyle = styled.div<{
  hasStockValueSetting: boolean;
}>`
  height: calc(100% - 73px);
  display: ${({ hasStockValueSetting }) => (hasStockValueSetting ? 'flex' : 'none')};
`;

export interface StockLevelPercentage {
  restockLevel: number;
  alarmLevel: number;
}

const ItemDetailPage = () => {
  const goBack = usePageGoBack(PATH.ITEMS);
  const params = useParams<any>();

  const [item, setItem] = useState<Item | null>(null);
  const [isOpenStatusModal, setIsOpenStatusModal] = useState(false);
  const [isOpenUnableInActivateItemModal, setIsOpenUnableInActivateItemModal] = useState(false);
  const isItemActive = item?.status === StatusItem.Active;
  const isUnableInActivateItem = (item?.hasBin as number | undefined) === 0 ? false : true;
  const isLoading = !item?.id;

  const totalStockValueSetting = useNumber(0);
  const [stockPercentages, setStockPercentages] = useState<StockLevelPercentage>({
    restockLevel: 0,
    alarmLevel: 0,
  });
  const organization = useOrganizationStore((state) => state.state.organization);
  const organizationId = params.orgId ? Number(params.orgId) : organization?.id;

  useEffect(() => {
    if (!params.id) {
      goBack();
    }
  }, [params.id]);

  const { data: parLevelSetting } = useQuery({
    queryKey: ['parLevelSetting'],
    queryFn: () => getSetting(SettingType.criticalLevel),
    refetchOnWindowFocus: false,
  });

  const defaultParLevelSetting = useMemo(
    () => parLevelSetting?.find((el) => !el.organization),
    [parLevelSetting],
  );
  const orgParLevelSetting = useMemo(
    () => parLevelSetting?.find((el) => el.organization?.id === organizationId),
    [parLevelSetting, organizationId],
  );

  const defaultParValue = orgParLevelSetting?.value
    ? Number(orgParLevelSetting.value)
    : Number(defaultParLevelSetting?.value) || 0;

  const onClickBodyTable = (_row: Row) => () => {
    //do nothing
  };

  const getDataItemResponse = useCallback(async (newData: Item, total: number) => {
    const dataRes = (await getSetting(SettingType.stockLevel)) || [];
    const itemType =
      Number(newData?.stockType) === StockTypeEnum.Chargeable ? 'chargeable' : 'nonChargeable';
    const restockValue = dataRes.find((el) => el.name === StockLevelSettingType.restock);
    const alarmValue = dataRes.find((el) => el.name === StockLevelSettingType.alarm);
    setStockPercentages({
      restockLevel: restockValue ? restockValue.value[itemType] : 0,
      alarmLevel: alarmValue ? alarmValue.value[itemType] : 0,
    });
    setItem(newData);
    totalStockValueSetting.setValue(total);
  }, []);

  const handleToggleStatusModel = () => {
    if (isUnableInActivateItem) {
      setIsOpenUnableInActivateItemModal(true);
      return;
    }

    setIsOpenStatusModal(!isOpenStatusModal);
  };

  const handleCloseUnableInActivateItemModal = () => {
    setIsOpenUnableInActivateItemModal(false);
  };

  const handleGetItemTable = (query: string) => getItemTable(query, params.id, organizationId);

  const renderHeaderContent = () => {
    if (isLoading) {
      return (
        <div className="d-flex align-items-center">
          <IconRedirect style={{ cursor: 'pointer' }} onClick={goBack} />
          <Placeholder as="div" animation="glow" className="me-3">
            <Placeholder
              xs={6}
              style={{
                width: '170px',
                height: '24px',
                borderRadius: '4px',
              }}
            />
          </Placeholder>
          <Placeholder as="div" animation="glow">
            <Placeholder
              xs={6}
              style={{
                width: '68px',
                height: '38px',
                borderRadius: '4px',
                marginLeft: '24px',
              }}
            />
          </Placeholder>
        </div>
      );
    }

    return (
      <div className="d-flex align-items-center">
        <IconRedirect style={{ cursor: 'pointer' }} onClick={goBack} />
        <Typography variant="heading-6" className="me-3">
          {item?.name}
        </Typography>
        <StatusDefault
          label={StatusItem[Number(item?.status)]}
          isActive={isItemActive}
          styleMain={{
            height: 38,
            marginLeft: 24,
          }}
          styleNameStatus={{
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '26px',
          }}
        />
      </div>
    );
  };

  const renderActionButton = () => {
    if (isLoading) {
      return (
        <Placeholder as="div" animation="glow">
          <Placeholder.Button
            xs={3}
            style={{
              width: '100px',
              height: '38px',
              borderRadius: '6px',
            }}
          />
        </Placeholder>
      );
    }

    return (
      <Button variant={isItemActive ? 'danger' : 'success'} onClick={handleToggleStatusModel}>
        {isItemActive ? 'Inactivate' : 'Activate'}
      </Button>
    );
  };

  return (
    <RootContentPage
      title=""
      rootBody={{
        background: 'none',
      }}
      header={
        <div className="d-flex flex-column">
          <Header>
            {renderHeaderContent()}
            {renderActionButton()}
          </Header>
          <ShowInfoItemCard item={item} />
        </div>
      }
    >
      <Main>
        <AssignItemContainer>
          <LocationTitle>Location</LocationTitle>
          <AssignedItemCard />
        </AssignItemContainer>

        <ParLavelSettingContainer>
          <Typography
            variant="title-1"
            lineHeight={32}
            style={{
              paddingLeft: 28,
              marginBottom: 41,
            }}
          >
            Par Level Setting
          </Typography>

          <StockValueSettingTableContainerStyle
            hasStockValueSetting={!!totalStockValueSetting.value}
          >
            <TableComponent
              isHiddenHeader
              getList={handleGetItemTable}
              paramId={params.id}
              nameQuery={QUERIES.ITEMS_LIST}
              originColumns={stockValueSettingColumns(
                Number(params.id),
                stockPercentages.restockLevel,
                stockPercentages.alarmLevel,
                defaultParValue,
                item?.stockType || '',
              )}
              onClickBodyTable={onClickBodyTable}
              defaultState={{
                page: 1,
                limit: 10,
                orderBy: 'ward',
                orderDirection: 'ASC',
              }}
              isBorder={false}
              getDataItemResponse={getDataItemResponse}
              isRowClickable={false}
              spacing="none"
              tableContainerStyle={{
                paddingBottom: 32,
              }}
            />
          </StockValueSettingTableContainerStyle>

          <div
            className="align-items-center justify-content-center"
            style={{
              height: 200,
              display: totalStockValueSetting.value ? 'none' : 'flex',
            }}
          >
            <Typography>This item hasn't been assigned to any ward yet.</Typography>
          </div>
        </ParLavelSettingContainer>
      </Main>
      {!item?.id && <LoadingCard />}
      {isOpenStatusModal && (
        <ItemStatusModal
          item={item}
          setItem={setItem}
          open={isOpenStatusModal}
          onCancel={handleToggleStatusModel}
        />
      )}
      {isOpenUnableInActivateItemModal && (
        <UnableInactiveItemModal
          open={isOpenUnableInActivateItemModal}
          onCancel={handleCloseUnableInActivateItemModal}
        />
      )}
    </RootContentPage>
  );
};
export default ItemDetailPage;
